// Localized lookup for player names.
//
// Per I18N_WORKFLOW.md §3:
//   - Real players (from real-player-data.basketball.json) get hand-curated
//     translations in app/data/real-player-names.<lang>.json. The current `zh`
//     table is mainland Simplified Chinese; future region variants such as
//     `zh-TW` need separate tables because common basketball names differ by
//     locale.
//   - Generated/fictional players also need localized display names. Long term
//     those should be stored at creation time. Until that data exists, this file
//     provides deterministic locale-specific fallbacks so localized UI never
//     leaks English names just because a player is fictional.
//
// Lookup is by full English name because (1) PlayerNameLabels doesn't carry
// srID, (2) plumbing srID through 51 call sites is high-effort. Among famous
// players the full English name is unique. If a duplicate later appears,
// promote that pair to an srID-keyed mapping.
import realPlayerNamesZh from "../../../data/real-player-names.zh.json";
import i18n from "./i18n.ts";

const TABLES: Record<string, Record<string, string>> = {
	zh: realPlayerNamesZh as Record<string, string>,
};

const getLanguage = () => i18n.language.split("-")[0] ?? i18n.language;

const GENERATED_FIRST_NAMES_ZH: Record<string, string> = {
	Aaron: "阿龙",
	Adam: "亚当",
	Alex: "亚历克斯",
	Alexander: "亚历山大",
	Andre: "安德烈",
	Andrew: "安德鲁",
	Anthony: "安东尼",
	Arthur: "阿瑟",
	Austin: "奥斯汀",
	Barry: "巴里",
	Benjamin: "本杰明",
	Bob: "鲍勃",
	Brandon: "布兰登",
	Brian: "布莱恩",
	Carl: "卡尔",
	Charles: "查尔斯",
	Chase: "蔡斯",
	Chas: "查斯",
	Christian: "克里斯蒂安",
	Christopher: "克里斯托弗",
	Daniel: "丹尼尔",
	David: "大卫",
	Dennis: "丹尼斯",
	Donald: "唐纳德",
	Doron: "多伦",
	Douglas: "道格拉斯",
	Devin: "德文",
	Dwayne: "德维恩",
	Edward: "爱德华",
	Eric: "埃里克",
	Ethan: "伊桑",
	Frank: "弗兰克",
	Gary: "加里",
	George: "乔治",
	Gerald: "杰拉德",
	Harold: "哈罗德",
	Henry: "亨利",
	Jack: "杰克",
	Jacob: "雅各布",
	James: "詹姆斯",
	Jason: "杰森",
	Jeffrey: "杰弗里",
	Jeremy: "杰里米",
	Jerry: "杰里",
	John: "约翰",
	Jonathan: "乔纳森",
	Jose: "何塞",
	Joseph: "约瑟夫",
	Justin: "贾斯汀",
	Keith: "基思",
	Kevin: "凯文",
	Kyle: "凯尔",
	Larry: "拉里",
	Mark: "马克",
	Michael: "迈克尔",
	Miles: "迈尔斯",
	Milan: "米兰",
	Miran: "米兰",
	Myles: "迈尔斯",
	Nathan: "内森",
	Nicholas: "尼古拉斯",
	Patrick: "帕特里克",
	Paul: "保罗",
	Peter: "彼得",
	Raymond: "雷蒙德",
	Richard: "理查德",
	Robert: "罗伯特",
	Roger: "罗杰",
	Ronald: "罗纳德",
	Ryan: "瑞安",
	Sami: "萨米",
	Scott: "斯科特",
	Sean: "肖恩",
	Stephen: "斯蒂芬",
	Terry: "特里",
	Thomas: "托马斯",
	Tom: "汤姆",
	Tony: "托尼",
	Travis: "特拉维斯",
	Tunji: "通吉",
	Tunjay: "通杰",
	Tyler: "泰勒",
	Walter: "沃尔特",
	William: "威廉",
	Zachary: "扎卡里",
};

const GENERATED_LAST_NAMES_ZH: Record<string, string> = {
	Adams: "亚当斯",
	Allen: "艾伦",
	Anderson: "安德森",
	Antikainen: "安蒂凯宁",
	Baker: "贝克",
	Benjamin: "本杰明",
	Bowen: "鲍恩",
	Brown: "布朗",
	Bunting: "班廷",
	Buenting: "布恩廷",
	Campbell: "坎贝尔",
	Carter: "卡特",
	Clark: "克拉克",
	Davis: "戴维斯",
	Egan: "伊根",
	Evans: "埃文斯",
	Edmondson: "埃德蒙森",
	Esume: "埃苏梅",
	Garcia: "加西亚",
	Gillaspie: "吉拉斯皮",
	Glass: "格拉斯",
	Gonzalez: "冈萨雷斯",
	Goodman: "古德曼",
	Green: "格林",
	Harris: "哈里斯",
	Hernandez: "埃尔南德斯",
	Hill: "希尔",
	Hollinger: "霍林格",
	Jackson: "杰克逊",
	Johnson: "约翰逊",
	Jones: "琼斯",
	King: "金",
	Kingma: "金马",
	Knight: "奈特",
	Lee: "李",
	Lewis: "刘易斯",
	Lopez: "洛佩斯",
	Martin: "马丁",
	Martinez: "马丁内斯",
	Micic: "米西奇",
	Miller: "米勒",
	Misic: "米西奇",
	Mitchell: "米切尔",
	Moore: "摩尔",
	Nelson: "尼尔森",
	Parker: "帕克",
	Perez: "佩雷斯",
	Phillips: "菲利普斯",
	Isom: "伊索姆",
	Ramirez: "拉米雷斯",
	Roberts: "罗伯茨",
	Robinson: "罗宾逊",
	Rodriguez: "罗德里格斯",
	Sanchez: "桑切斯",
	Scott: "斯科特",
	Smith: "史密斯",
	Taylor: "泰勒",
	Thomas: "托马斯",
	Thompson: "汤普森",
	Turner: "特纳",
	Walker: "沃克",
	White: "怀特",
	Williams: "威廉姆斯",
	Wilson: "威尔逊",
	Wright: "赖特",
	Young: "杨",
};

const PART_ZH: Record<string, string> = {
	a: "阿",
	ab: "阿布",
	ac: "阿克",
	ad: "阿德",
	ai: "艾",
	al: "阿尔",
	am: "阿姆",
	an: "安",
	ar: "阿尔",
	as: "阿斯",
	ash: "阿什",
	ba: "巴",
	be: "贝",
	ben: "本",
	ber: "伯",
	bo: "博",
	bow: "鲍",
	bra: "布拉",
	bran: "布兰",
	bre: "布雷",
	bri: "布里",
	bro: "布罗",
	bry: "布赖",
	ca: "卡",
	car: "卡尔",
	ce: "塞",
	cha: "查",
	che: "切",
	chi: "奇",
	chris: "克里斯",
	co: "科",
	con: "康",
	cor: "科尔",
	cra: "克雷",
	da: "达",
	dan: "丹",
	de: "德",
	del: "德尔",
	di: "迪",
	do: "多",
	don: "唐",
	du: "杜",
	ed: "埃德",
	el: "埃尔",
	en: "恩",
	er: "埃尔",
	fa: "法",
	fi: "菲",
	fo: "福",
	fra: "弗拉",
	fran: "弗兰",
	ga: "加",
	gar: "加尔",
	ge: "杰",
	geo: "乔",
	gil: "吉尔",
	glen: "格伦",
	go: "戈",
	good: "古德",
	gra: "格拉",
	green: "格林",
	ha: "哈",
	har: "哈尔",
	he: "赫",
	hen: "亨",
	hi: "希",
	ho: "霍",
	ia: "亚",
	ian: "伊恩",
	ja: "贾",
	jack: "杰克",
	jam: "詹",
	je: "杰",
	jo: "乔",
	john: "约翰",
	ju: "朱",
	ka: "卡",
	ke: "凯",
	ken: "肯",
	ki: "基",
	la: "拉",
	lar: "拉尔",
	le: "勒",
	lee: "李",
	li: "利",
	lo: "洛",
	lu: "卢",
	ma: "马",
	mar: "马尔",
	me: "梅",
	mi: "米",
	mich: "迈克",
	mo: "莫",
	na: "纳",
	ne: "内",
	nel: "内尔",
	ni: "尼",
	no: "诺",
	pa: "帕",
	par: "帕尔",
	pe: "佩",
	phil: "菲尔",
	ra: "拉",
	ram: "拉姆",
	re: "雷",
	ri: "里",
	ric: "里克",
	ro: "罗",
	rob: "罗布",
	rod: "罗德",
	ron: "罗恩",
	ry: "赖",
	sa: "萨",
	san: "桑",
	scot: "斯科特",
	se: "塞",
	sha: "沙",
	si: "西",
	smith: "史密斯",
	so: "索",
	sta: "斯塔",
	ste: "斯特",
	ta: "塔",
	tay: "泰",
	te: "特",
	ter: "特尔",
	thom: "汤姆",
	ti: "蒂",
	to: "托",
	ton: "托尼",
	tris: "特里斯",
	tu: "图",
	ty: "泰",
	va: "瓦",
	ve: "维",
	vi: "维",
	wa: "瓦",
	wal: "沃尔",
	we: "韦",
	wi: "威",
	wil: "威尔",
	ya: "亚",
	yo: "约",
	za: "扎",
};

const LETTER_ZH: Record<string, string> = {
	a: "艾",
	b: "比",
	c: "西",
	d: "迪",
	e: "伊",
	f: "夫",
	g: "吉",
	h: "赫",
	i: "伊",
	j: "杰",
	k: "克",
	l: "尔",
	m: "姆",
	n: "恩",
	o: "奥",
	p: "普",
	q: "丘",
	r: "尔",
	s: "斯",
	t: "特",
	u: "尤",
	v: "维",
	w: "沃",
	x: "克斯",
	y: "伊",
	z: "兹",
};

const ENDINGS_ZH: [string, string][] = [
	["ville", "维尔"],
	["field", "菲尔德"],
	["wood", "伍德"],
	["worth", "沃思"],
	["son", "森"],
	["sen", "森"],
	["man", "曼"],
	["men", "门"],
	["ton", "顿"],
	["den", "登"],
	["ley", "利"],
	["ly", "利"],
	["er", "尔"],
	["ers", "尔斯"],
	["es", "斯"],
	["s", "斯"],
	["y", "伊"],
	["a", "阿"],
	["e", ""],
];

const generatedNameCacheZh = new Map<string, string>();

const splitNameParts = (name: string) =>
	name
		.replaceAll("'", "")
		.split(/[-\s]+/)
		.map((part) => part.trim())
		.filter(Boolean);

const transliterateUnknownNamePartZh = (name: string) => {
	const lower = name.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
	let remaining = lower;
	let ending = "";

	for (const [suffix, translated] of ENDINGS_ZH) {
		if (remaining.length > suffix.length + 1 && remaining.endsWith(suffix)) {
			remaining = remaining.slice(0, -suffix.length);
			ending = translated;
			break;
		}
	}

	let out = "";
	while (remaining.length > 0) {
		let matched = false;
		for (let length = Math.min(remaining.length, 6); length > 0; length -= 1) {
			const chunk = remaining.slice(0, length);
			const translated = PART_ZH[chunk];
			if (translated !== undefined) {
				out += translated;
				remaining = remaining.slice(length);
				matched = true;
				break;
			}
		}

		if (!matched) {
			out += LETTER_ZH[remaining[0]!] ?? remaining[0]!;
			remaining = remaining.slice(1);
		}
	}

	return out + ending || name;
};

const transliterateNamePartZh = (
	name: string,
	table: Record<string, string>,
) => {
	const direct = table[name];
	if (direct) return direct;

	return splitNameParts(name)
		.map((part) => table[part] ?? transliterateUnknownNamePartZh(part))
		.join("");
};

const translateGeneratedPlayerNameZh = (
	firstName: string,
	lastName: string,
) => {
	const fullName = `${firstName} ${lastName}`.trim();
	const cached = generatedNameCacheZh.get(fullName);
	if (cached) return cached;

	const suffixMatch = lastName.match(/\s+((?:Jr|Sr)\.?)$/);
	const lastNameBase = suffixMatch
		? lastName.slice(0, suffixMatch.index).trim()
		: lastName;
	const suffix = suffixMatch
		? suffixMatch[1]!.startsWith("Jr")
			? "小"
			: "老"
		: "";

	const translatedFirstName = transliterateNamePartZh(
		firstName,
		GENERATED_FIRST_NAMES_ZH,
	);
	const translatedLastName = transliterateNamePartZh(
		lastNameBase,
		GENERATED_LAST_NAMES_ZH,
	);
	const translated = `${translatedFirstName}·${suffix}${translatedLastName}`;
	generatedNameCacheZh.set(fullName, translated);
	return translated;
};

export const translatePlayerName = (
	firstName: string,
	lastName: string,
): { firstName: string; lastName: string } => {
	const language = getLanguage();
	const table = TABLES[language];
	const fullName = `${firstName} ${lastName}`.trim();
	if (!table) return { firstName, lastName };
	const translated = table[fullName];
	const displayName =
		translated ??
		(language === "zh"
			? translateGeneratedPlayerNameZh(firstName, lastName)
			: undefined);
	if (!displayName) return { firstName, lastName };
	// Chinese names render as a single string with no first/last separation.
	// Put the whole thing in lastName so consumers that render "first last"
	// produce a clean "translated" output (firstName empty trims away).
	return { firstName: "", lastName: displayName };
};

export const parseLegacyPlayerName = (name: string) => {
	// Used to be `const parts = name.split(" (")[0].split(" ");`, not sure why,
	// but that messes up names with parentheses in them.
	const parts = name.split(" ");
	let lastName = parts.at(-1)!;
	let lastNameIndex = parts.length - 1;

	// For "Bob Smith Jr." and similar names, return "Smith Jr." as the last
	// name so lookup/transliteration can preserve the suffix.
	const suffixes = ["Jr", "Jr.", "Sr", "Sr."];

	if (
		parts.length > 2 &&
		(suffixes.includes(lastName) || lastName === lastName.toUpperCase())
	) {
		lastName = parts.slice(-2).join(" ");
		lastNameIndex = parts.length - 2;
	}
	const firstName = parts.slice(0, lastNameIndex).join(" ");

	return {
		firstName,
		lastName,
	};
};

// Convenience for sites that just want "first last" as a single string.
// Always run inputs through translatePlayerName first.
// `firstNameDisplay` (optional) is the visual form to use when *no* translation
// applies (e.g. "K." instead of "Kevin"). Lookup always uses the full
// `firstName` since the zh table is keyed by full English name.
export const formatPlayerName = (
	firstName: string,
	lastName: string,
	firstNameDisplay?: string,
): string => {
	const t = translatePlayerName(firstName, lastName);
	if (t.firstName === "" && t.lastName !== `${firstName} ${lastName}`.trim()) {
		// Translation hit — return single localized string.
		return t.lastName;
	}
	const display = firstNameDisplay ?? firstName;
	return display ? `${display} ${lastName}` : lastName;
};

export const formatPlayerNameFromLegacyName = (name: string): string => {
	const { firstName, lastName } = parseLegacyPlayerName(name);
	return formatPlayerName(firstName, lastName);
};
